import { useState, useMemo, useEffect } from 'react';
import type React from 'react';
import { Box, Text, Flex, useMediaQuery } from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { OptimizedVolunteer } from '@/utils/optimizedQueries';
import { splitDepartments } from '@/utils/departments';
import { departmentColor } from '@/utils/departmentColors';
import { VolunteerCard } from './components';

interface VolunteersDepartmentsViewProps {
  volunteers: OptimizedVolunteer[];
  loading: boolean;
  /** The event's own department list, so a department with nobody in it still has a card. */
  eventDepartments?: string[];
  onManageClick?: (volunteer: OptimizedVolunteer) => void;
  onMessageClick?: (volunteer: OptimizedVolunteer) => void;
  departmentColors?: Record<string, string>;
  onUpdateVolunteerDepartment?: (volunteerId: string, department: string | null) => Promise<void>;
  onDepartmentError?: (message: string) => void;
}

export function VolunteersDepartmentsView({
  volunteers,
  loading,
  eventDepartments,
  onManageClick,
  onMessageClick,
  departmentColors,
  onUpdateVolunteerDepartment,
  onDepartmentError,
}: VolunteersDepartmentsViewProps) {
  const [expandedDepts, setExpandedDepts] = useState<Record<string, boolean>>({});
  const [isDesktop] = useMediaQuery(['(min-width: 1024px)']);
  const UNASSIGNED_DROP_KEY = 'Unassigned';
  const [dragOverDept, setDragOverDept] = useState<string | null>(null);

  const { unassigned, departments } = useMemo(() => {
    // Grouped by the lowercased name, like every other reader of `volunteers.department`
    // (see utils/departments.ts), so the event's "Logistics" and a volunteer's "logistics"
    // are one card rather than two -- one of which would look empty.
    const byKey = new Map<string, { label: string; items: OptimizedVolunteer[] }>();
    const unassignedList: OptimizedVolunteer[] = [];

    // Seed the event's own departments first, with no volunteers in them. A department the
    // organization created stays on the board after its last volunteer is moved out --
    // otherwise moving that one person away makes the department look deleted, and there is
    // nothing left to drag anyone back onto. Seeding first also means the label shown is the
    // organization's spelling, not whichever volunteer happened to be listed first.
    for (const raw of eventDepartments ?? []) {
      for (const dept of splitDepartments(raw)) {
        const key = dept.toLowerCase();
        if (!byKey.has(key)) byKey.set(key, { label: dept, items: [] });
      }
    }

    for (const v of volunteers) {
      // Allow multiple departments per volunteer, and avoid creating a combined
      // label like "Dept A, Dept B".
      const labels = (v.departments ?? []).flatMap((d) =>
        splitDepartments(typeof d === 'string' ? d : String(d ?? '')),
      );

      if (labels.length === 0) {
        unassignedList.push(v);
        continue;
      }

      const placed = new Set<string>();
      for (const label of labels) {
        const key = label.toLowerCase();
        if (placed.has(key)) continue;
        placed.add(key);
        const group = byKey.get(key);
        if (group) group.items.push(v);
        else byKey.set(key, { label, items: [v] });
      }
    }

    const deptEntries = [...byKey.values()]
      .map(({ label, items }) => [label, items] as [string, OptimizedVolunteer[]])
      .sort(([a], [b]) => a.localeCompare(b));

    return {
      unassigned: unassignedList,
      departments: deptEntries,
    };
  }, [volunteers, eventDepartments]);

  // Initialize expanded state based on viewport: open all on desktop, collapsed on mobile/tablet.
  // Preserve user toggles; only seed defaults when we don't have state yet or when new departments appear.
  useEffect(() => {
    const defaults: Record<string, boolean> = {};
    for (const [deptName] of departments) {
      defaults[deptName] = !!isDesktop;
    }

    setExpandedDepts((prev) => {
      const hasPrev = Object.keys(prev).length > 0;
      if (!hasPrev) {
        return defaults;
      }

      const next = { ...prev };
      for (const [deptName] of departments) {
        if (!(deptName in next)) {
          next[deptName] = defaults[deptName];
        }
      }
      return next;
    });
  }, [departments, isDesktop]);

  if (loading) {
    return (
      <Box mt="2" display="flex" flexDirection="column" gap="3">
        {[0, 1, 2].map((key) => (
          <Box
            key={key}
            bg="volyx.bg"
            borderRadius="xl"
            boxShadow="0 6px 18px rgba(0,0,0,0.12)"
            p="4"
          >
            <Flex alignItems="center" gap="3">
              <Box w="24px" h="24px" bg="volyx.lightGray" borderRadius="md" />
              <Box flex="1">
                <Box h="4" w="40%" mb="2" bg="volyx.lightGray" borderRadius="md" />
                <Box h="3" w="20%" bg="volyx.lightGray" borderRadius="md" />
              </Box>
            </Flex>
          </Box>
        ))}
      </Box>
    );
  }

  // Departments the event defines are worth showing even before anyone joins -- they are
  // the drop targets. Only an event with neither volunteers nor departments is empty.
  if (!volunteers.length && !departments.length) {
    return (
      <Box mt="6" textAlign="center" py="10">
        <Text fontSize="sm" color="volyx.textSecondary">No volunteers to group by department.</Text>
      </Box>
    );
  }

  const toggleDept = (deptName: string) => {
    setExpandedDepts((prev) => ({ ...prev, [deptName]: !prev[deptName] }));
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, volunteerId: string) => {
    e.dataTransfer.setData('volunteer-id', volunteerId);
    e.dataTransfer.setData('text/plain', volunteerId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDragOverDept(null);
  };

  const handleDropOnDepartment = async (
    e: React.DragEvent<HTMLDivElement>,
    deptName: string | null,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDept(null);
    const volunteerId = e.dataTransfer.getData('volunteer-id') || e.dataTransfer.getData('text/plain');
    if (!volunteerId) return;

    const nextDepartment = deptName && deptName !== UNASSIGNED_DROP_KEY ? deptName : null;

    const volunteer = volunteers.find((v) => v.id === volunteerId);
    if (!volunteer) return;

    const currentDepartments = volunteer.departments ?? [];
    const alreadyThere = nextDepartment
      ? currentDepartments.includes(nextDepartment)
      : currentDepartments.length === 0;
    if (alreadyThere) return;

    if (!onUpdateVolunteerDepartment) return;

    try {
      await onUpdateVolunteerDepartment(volunteerId, nextDepartment);
    } catch (err) {
      onDepartmentError?.(
        err instanceof Error ? err.message : 'Failed to update department',
      );
    }
  };

  const getDropZoneProps = (deptKey: string) => ({
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverDept(deptKey);
    },
    onDragLeave: () => {
      setDragOverDept((prev) => (prev === deptKey ? null : prev));
    },
    onDrop: (e: React.DragEvent<HTMLDivElement>) => handleDropOnDepartment(e, deptKey),
  });

  const renderDraggableVolunteer = (volunteer: OptimizedVolunteer, dropDeptKey: string) => (
    <Box
      key={volunteer.id}
      draggable
      onDragStart={(e) => handleDragStart(e, volunteer.id)}
      onDragEnd={handleDragEnd}
      cursor={isDesktop ? 'grab' : undefined}
      _active={isDesktop ? { cursor: 'grabbing' } : undefined}
      borderRadius="lg"
      bg={dragOverDept === dropDeptKey ? 'volyx.lightGray' : 'transparent'}
      {...getDropZoneProps(dropDeptKey)}
    >
      <VolunteerCard
        volunteer={volunteer}
        onManageClick={onManageClick}
        onMessageClick={onMessageClick}
        compact
      />
    </Box>
  );

  return (
    <Box mt="2">
      <Box
        mb="4"
        bg="volyx.bg"
        borderRadius="xl"
        boxShadow="0 6px 18px rgba(0,0,0,0.12)"
        overflow="hidden"
      >
        <Flex
          alignItems="center"
          gap="2"
          p="4"
          bg={dragOverDept === UNASSIGNED_DROP_KEY ? 'volyx.lightGray' : 'transparent'}
          {...getDropZoneProps(UNASSIGNED_DROP_KEY)}
        >
          <ChevronRight size={20} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
          <Box
            w="10px"
            h="10px"
            borderRadius="full"
            bg="volyx.textSecondary"
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" m="0">
            Unassigned
          </Text>
          <Text fontSize="xs" color="volyx.textSecondary" m="0">
            ({unassigned.length})
          </Text>
        </Flex>
        {unassigned.length > 0 && (
          <Box
            borderTop="1px solid"
            borderColor="volyx.lightGray"
            p="3"
            pt="0"
            display="flex"
            flexDirection="column"
            gap="2"
          >
            {unassigned.map((volunteer) => renderDraggableVolunteer(volunteer, UNASSIGNED_DROP_KEY))}
          </Box>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap="3">
        {departments.map(([deptName, list]) => {
          const isExpanded = !!expandedDepts[deptName];
          // The map is built from the pie chart, which only knows departments that have
          // volunteers; an empty one falls back to its own hashed colour rather than grey.
          const color = departmentColors?.[deptName] ?? departmentColor(deptName);
          return (
            <Box
              key={deptName}
              bg="volyx.bg"
              borderRadius="xl"
              boxShadow="0 6px 18px rgba(0,0,0,0.12)"
              overflow="hidden"
            >
              <Flex
                alignItems="center"
                gap="2"
                p="4"
                cursor="pointer"
                bg={dragOverDept === deptName ? 'volyx.lightGray' : 'transparent'}
                onClick={() => toggleDept(deptName)}
                _hover={{ bg: 'volyx.lightGray' }}
                {...getDropZoneProps(deptName)}
              >
                {isExpanded ? (
                  <ChevronDown size={20} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
                ) : (
                  <ChevronRight size={20} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
                )}
                <Box
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg={color}
                  flexShrink={0}
                />
                <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" m="0">{deptName}</Text>
                <Text fontSize="xs" color="volyx.textSecondary" m="0">({list.length})</Text>
              </Flex>
              {isExpanded && (
                <Box
                  borderTop="1px solid"
                  borderColor="volyx.lightGray"
                  p="3"
                  pt="0"
                  display="flex"
                  flexDirection="column"
                  gap="2"
                >
                  {list.map((volunteer) => renderDraggableVolunteer(volunteer, deptName))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
